import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import type { TFunction } from 'i18next'
import React from 'react'

import type { NormalizedStatisticsApiRow } from './filterableRecord'

const GAUSIUM_COMPLETED_MIN_PERCENT = 85

function completionToPercent(raw: number): number {
  if (!Number.isFinite(raw)) return raw
  if (raw >= 0 && raw <= 1) return raw * 100
  return raw
}

function isOlderThan24h(row: NormalizedStatisticsApiRow): boolean {
  const startTimeStr = row.start_time || row.created_at
  if (startTimeStr) {
    try {
      const startTime = new Date(String(startTimeStr).replace(' ', 'T'))
      if (!Number.isNaN(startTime.getTime())) {
        if (Date.now() - startTime.getTime() > 24 * 60 * 60 * 1000) {
          return true
        }
      }
    } catch {
      // Ignore parse errors and fall through to active/in-progress handling.
    }
  }
  return false
}

export function renderNormalizedExecutionStatusIcon(row: NormalizedStatisticsApiRow): React.ReactNode {
  const code = row.execution_status

  if (code != null && Number.isFinite(Number(code))) {
    const n = Number(code)
    switch (n) {
      case 4:
        return React.createElement(TaskAltIcon, { fontSize: 'small', color: 'success' })
      case 7:
        return React.createElement(StopCircleOutlinedIcon, { fontSize: 'small', color: 'error' })
      case 8:
        return React.createElement(WarningAmberIcon, { fontSize: 'small', color: 'warning' })
      case 10:
        return React.createElement(BlockOutlinedIcon, { fontSize: 'small', color: 'error' })
      case 255:
        return React.createElement(HelpOutlineIcon, { fontSize: 'small', color: 'disabled' })
      case 99:
        return null
    }
  }

  if (isOlderThan24h(row)) {
    return React.createElement(HelpOutlineIcon, { fontSize: 'small', color: 'disabled' })
  }

  if (row.vendor_origin_record_type === 'gausium_task_report' && row.completion_percentage != null) {
    const p = completionToPercent(Number(row.completion_percentage))
    if (!Number.isFinite(p)) return React.createElement(HourglassEmptyIcon, { fontSize: 'small', color: 'info' })
    if (p >= GAUSIUM_COMPLETED_MIN_PERCENT) {
      return React.createElement(TaskAltIcon, { fontSize: 'small', color: 'success' })
    }
    return React.createElement(StopCircleOutlinedIcon, { fontSize: 'small', color: 'error' })
  }

  return React.createElement(HourglassEmptyIcon, { fontSize: 'small', color: 'info' })
}

export function getNormalizedExecutionStatusText(row: NormalizedStatisticsApiRow, t: TFunction): string {
  const code = row.execution_status

  if (code != null && Number.isFinite(Number(code))) {
    const n = Number(code)
    switch (n) {
      case 4:
        return t('robot.history_status_completed', 'Completed')
      case 7:
        return t('robot.history_status_canceled', 'Canceled by user')
      case 8:
        return t('robot.history_status_aborted', 'Aborted by robot')
      case 10:
        return t('robot.history_status_cannot_be_started', 'The task was not started')
      case 255:
        return t('robot.history_status_unknown', 'Unknown')
      case 99:
        return t('robot.history_status_no_statistic', 'No statistics')
    }
  }

  if (isOlderThan24h(row)) {
    return t('robot.history_status_unknown', 'Unknown')
  }

  if (row.vendor_origin_record_type === 'gausium_task_report' && row.completion_percentage != null) {
    const p = completionToPercent(Number(row.completion_percentage))
    if (!Number.isFinite(p)) return '—'
    if (p >= GAUSIUM_COMPLETED_MIN_PERCENT) return t('robot.history_status_completed', 'Completed')
    return t('robot.history_status_canceled', 'Canceled by user')
  }

  return t('robot.history_status_in_progress', 'In progress')
}
